import React from "react";
import {
  Button,
  Space,
  Popconfirm,
  Upload,
  Select,
  Tag,
  Spin,
  message,
} from "antd";
import {
  EditOutlined,
  CheckOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
  UploadOutlined,
  DownloadOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";

// Action button component for cleaner code
const ActionButton = ({ 
  icon, 
  text, 
  onClick, 
  type = "default", 
  danger = false, 
  style = {}, 
  disabled = false,
  size = "middle"
}) => (
  <Button
    type={type}
    danger={danger}
    onClick={onClick}
    style={style}
    disabled={disabled}
    icon={icon}
    size={size}
  >
    {text}
  </Button>
);

// Creator Actions Component
export const CreatorActions = ({
  voucher,
  currentUser,
  onEdit,
  onSubmit,
  onDelete,
}) => {
  if (currentUser.id !== voucher.creator.id || voucher.submitted) return null;

  return (
    <Space wrap>
      <ActionButton
        icon={<EditOutlined />}
        text="Edit"
        onClick={onEdit}
        type="primary"
      />
      <ActionButton
        icon={<CheckOutlined />}
        text="Submit"
        onClick={onSubmit}
        type="primary"
        style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
      />
      <Popconfirm
        title="Delete Voucher"
        description="Are you sure you want to delete this voucher?"
        onConfirm={onDelete}
        okText="Yes"
        cancelText="No"
      >
        <ActionButton
          icon={<DeleteOutlined />}
          text="Delete"
          type="primary"
          danger
        />
      </Popconfirm>
    </Space>
  );
};

// Approval Actions Component
export const ApprovalActions = ({
  voucher,
  currentUser,
  onApprove,
  onReject,
}) => {
  if (
    !voucher?.submitted ||
    !voucher.nextApprovalStage ||
    !currentUser?.roles?.includes(voucher.nextApprovalStage.role)
  ) {
    return null;
  }

  return (
    <Space wrap>
      <ActionButton
        icon={<CheckOutlined />}
        text={`Approve as ${voucher.nextApprovalStage.role.replace("_", " ")}`}
        onClick={onApprove}
        type="primary"
        style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
      />
      <ActionButton
        icon={<CloseCircleOutlined />}
        text="Reject"
        onClick={onReject}
        type="primary"
        danger
      />
    </Space>
  );
};

// Cash Office Actions Component
export const CashOfficeActions = ({
  voucher,
  currentUser,
  onChequeToggle,
  onClose,
}) => {
  if (!currentUser?.roles?.includes("CASH_OFFICE")) return null;

  return (
    <Space wrap>
      {voucher.finalApproved && !voucher.settled && (
        <ActionButton
          icon={voucher.chequeReady ? <CloseCircleOutlined /> : <CheckOutlined />}
          text={voucher.chequeReady ? "Cheque not ready" : "Cheque Ready"}
          onClick={onChequeToggle}
          type="primary"
          danger={voucher.chequeReady}
          style={!voucher.chequeReady ? { backgroundColor: "#52c41a", borderColor: "#52c41a" } : {}}
        />
      )}
      {voucher.retired && !voucher.closed && (
        <Popconfirm
          title="Close Voucher"
          description="Are you sure you want to close this voucher?"
          onConfirm={onClose}
          okText="Yes"
          cancelText="No"
        >
          <ActionButton
            icon={<CloseCircleOutlined />}
            text="Close Voucher"
            type="primary"
            danger
          />
        </Popconfirm>
      )}
    </Space>
  );
};

// Bursar Actions Component
export const BursarActions = ({
  voucher,
  currentUser,
  onSettle,
}) => {
  if (
    !voucher.finalApproved ||
    !voucher.chequeReady ||
    voucher.closed ||
    !currentUser?.roles?.includes("BURSAR")
  ) {
    return null;
  }

  return (
    <Popconfirm
      title="Settle Voucher"
      description="Are you sure you want to settle this voucher? This cannot be undone!"
      onConfirm={onSettle}
      okText="Yes"
      cancelText="No"
    >
      <ActionButton
        icon={<CheckOutlined />}
        text="Settle Voucher"
        type="primary"
        style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
      />
    </Popconfirm>
  );
};

// Checker Actions Component
export const CheckerActions = ({
  voucher,
  currentUser,
  onCheck,
}) => {
  if (
    voucher.checker?.id !== currentUser.id ||
    !voucher.submitted ||
    voucher.checked
  ) {
    return null;
  }

  return (
    <Popconfirm
      title="Check Voucher"
      description="Are you sure you want to check this voucher?"
      onConfirm={onCheck}
      okText="Yes"
      cancelText="No"
    >
      <ActionButton
        icon={<CheckOutlined />}
        text="Check"
        type="primary"
        style={{ backgroundColor: "#722ed1", borderColor: "#722ed1" }}
      />
    </Popconfirm>
  );
};

// Acknowledgment Actions Component
export const AcknowledgmentActions = ({
  voucher,
  currentUser,
  onAcknowledge,
}) => {
  if (
    !voucher.settled ||
    voucher.memo.creator.id !== currentUser.id ||
    voucher.acknowledgedDate
  ) {
    return null;
  }

  return (
    <Popconfirm
      title="Acknowledge Payment"
      description="Are you sure you want to acknowledge this payment?"
      onConfirm={onAcknowledge}
      okText="Yes"
      cancelText="No"
    >
      <ActionButton
        icon={<CheckOutlined />}
        text="Acknowledge Payment"
        type="primary"
        style={{ backgroundColor: "#1890ff", borderColor: "#1890ff" }}
      />
    </Popconfirm>
  );
};

// Retirement Document Actions Component
export const RetirementActions = ({
  voucher,
  currentUser,
  onUpload,
  onDownload,
}) => {
  const canUpload =
    currentUser.id === voucher.memo.creator.id &&
    !voucher.closed &&
    voucher.settled &&
    voucher.acknowledgedDate;

  return (
    <Space wrap>
      {canUpload && (
        <Upload
          showUploadList={false}
          customRequest={onUpload}
          beforeUpload={(file) => {
            const isPdfFile = file.type === "application/pdf";
            if (!isPdfFile) {
              message.error(`${file.name} is not a PDF file`);
              return false;
            }
            return true;
          }}
        >
          <ActionButton
            icon={<UploadOutlined />}
            text="Upload Retirement"
            type="default"
          />
        </Upload>
      )}
      <ActionButton
        icon={<DownloadOutlined />}
        text="Download Retirement"
        onClick={onDownload}
        type="default"
      />
    </Space>
  );
};

// Document Preview Actions Component
export const DocumentActions = ({
  onPreviewMemo,
  onPreviewVoucher,
}) => (
  <Space wrap>
    <ActionButton
      icon={<FilePdfOutlined />}
      text="Preview Memo"
      onClick={onPreviewMemo}
      type="default"
    />
    <ActionButton
      icon={<FilePdfOutlined />}
      text="Preview Voucher"
      onClick={onPreviewVoucher}
      type="default"
    />
  </Space>
);

// Checker Selection Component
export const CheckerSelection = ({
  voucher,
  currentUser,
  checkerOptions,
  checkerSearchLoading,
  onSearch,
  onSelect,
  onSetChecker,
  selectedChecker,
}) => {
  if (currentUser?.id !== voucher.creator.id || voucher.submitted) {
    return null;
  }

  return (
    <Space>
      <Select
        showSearch
        placeholder="Search for a checker"
        style={{ width: 250 }}
        defaultActiveFirstOption={false}
        showArrow={false}
        filterOption={false}
        onSearch={onSearch}
        onChange={onSelect}
        notFoundContent={checkerSearchLoading ? <Spin size="small" /> : null}
        options={checkerOptions}
        loading={checkerSearchLoading}
      />
      <ActionButton
        text="Set Checker"
        onClick={onSetChecker}
        type="primary"
        disabled={!selectedChecker}
      />
    </Space>
  );
};

// Info Display Components
export const CheckerInfo = ({ checker }) => {
  if (!checker) return null;

  return (
    <div>
      <span style={{ marginRight: "8px" }}>Checker:</span>
      <Tag color="blue">
        {checker.name} (ID: {checker.userId})
      </Tag>
    </div>
  );
};

export const ChequeInfo = ({ chequeNumber }) => {
  if (!chequeNumber) return null;

  return (
    <div>
      <span style={{ marginRight: "8px" }}>Cheque Number:</span>
      <Tag color="green">{chequeNumber}</Tag>
    </div>
  );
};